import {
  Message,
  Button,
  Grid,
  Heading,
  Text,
  Theme,
  useIsTheme,
  ButtonKinds,
  ButtonProps,
  ButtonSizes,
  Link,
  MessageInline,
  ToastItem,
  useToast,
} from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode, useEffect } from 'react'
import Image from 'next/image'
import { AmplitudeErrorEventNames, isShippingOptionErrorType, ShippingAddress, ShippingOption, ShippingOptionErrorType, TerminalErrorCode } from '../../model'
import { BASE_PATH } from '../../env'
import { ModuleContainer, Trans } from '..'
import styles from './style.module.css'
import { useTranslation } from '../../utils/convergence'
import {
  useCheckoutState,
  useOtbAmount,
  useShippingOption,
  useIsDeferredShipping,
  useAppRouter,
  useInlineArcadeComponentsEnabled,
  useIsShippingOptionAddressError,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { CloseCheckout } from '../CloseCheckout'
import { Route } from '../../config/router'
import { Money } from '@afterpay/types'

interface Props {
  error: ShippingOptionErrorType | TerminalErrorCode
  isPickup?: boolean
  handleRetryButton?: (option?: ShippingOption) => void
}

interface ShippingOptionErrorEventProps {
  otb?: Money
  chosenAddress?: ShippingAddress
}

export const ShippingOptionError: FunctionComponent<Props> = ({ error, isPickup = false, handleRetryButton = () => {} }) => {
  const { checkout } = useCheckoutState()
  const { lastSelectedShippingOption } = useShippingOption()
  const isDeferredShipping = useIsDeferredShipping()
  const otbAmount = useOtbAmount()
  const { t } = useTranslation()
  const emptyBoxSrc = [BASE_PATH, 'images/empty-box.svg'].join('/')
  const { smallTextSize } = useCheckoutSizes()
  const { logEvent, instrument } = useMonitoringWithEnduringEventProperties()
  const isAPTheme = useIsTheme(Theme.AP)
  const router = useAppRouter()
  const isShippingAddressError = useIsShippingOptionAddressError()
  const isInlineArcadeComponentsEnabled = useInlineArcadeComponentsEnabled()
  const setToast = useToast()
  const validToastErrors = [ShippingOptionErrorType.ShippingOptionUnableToUpdate, TerminalErrorCode.UnableToUpdateCheckout]

  const handleBackToStoreButtonClick = (): void => {
    logEvent(TrackingEvent.PRESSED_BACK_TO_STORE, { error })
  }

  const handleRetryClick = isDeferredShipping
    ? () => handleRetryButton()
    : instrument(TrackingEvent.MERCHANT_SHIPPING_OPTION_RETRY, () => handleRetryButton(lastSelectedShippingOption))

  const toastMessage = (): ReactNode => {
    return (
      <>
        <ToastItem.Headline>{t('shippingOption:error.unableToUpdate.heading_inline')}</ToastItem.Headline>
        <ToastItem.Body>{t('shippingOption:error.unableToUpdate.message_inline')}</ToastItem.Body>
      </>
    )
  }

  useEffect(() => {
    if (isInlineArcadeComponentsEnabled && validToastErrors.includes(error)) {
      setToast({
        message: toastMessage,
        kind: 'Failure',
        testNameSpace: 'shipping-option-error',
        linkLabel: t('shippingOption:retry'),
        toastTime: 10000,
        linkUrl: handleRetryClick,
      })
    }
  }, [isInlineArcadeComponentsEnabled, error])

  const showTotalTooHighError = (): ReactNode =>
    basicShippingOptionError('shippingOption:error.totalTooHigh.heading', 'shippingOption:error.totalTooHigh.message')

  const showPickupError = (): ReactNode => basicShippingOptionError('shippingOption:error.pickup.heading', 'shippingOption:error.pickup.message')

  const showPickupTotalTooHighError = (): ReactNode =>
    basicShippingOptionError('shippingOption:error.pickupTotalTooHigh.heading', 'shippingOption:error.pickupTotalTooHigh.message')

  const showDefaultShippingOptionError = (): ReactNode =>
    basicShippingOptionError('shippingOption:error.default.heading', 'shippingOption:error.default.message')

  const basicShippingOptionError = (headerKey: string, errorKey: string): ReactNode => {
    const closeButtonProps: ButtonProps = isAPTheme
      ? { kind: 'Error' as ButtonKinds, size: 'Small' as ButtonSizes }
      : { kind: 'ErrorSecondary' as ButtonKinds, padding: 'Fluid' }

    if (isInlineArcadeComponentsEnabled) {
      return (
        <MessageInline
          kind='Error'
          description={
            <Text size={smallTextSize}>
              <Trans
                components={{
                  retryLink: <Link onClick={handleRetryClick} kind='Underline' />,
                }}
                i18nKey={errorKey}
                values={{ merchantName: checkout?.merchant?.name }}
                context='inline'
              />
            </Text>
          }
          testNameSpace='shipping-option-error'
        />
      )
    }

    return (
      <div className={styles.containerMargin}>
        <Message
          testNameSpace='shipping-option-error'
          kind='Error'
          heading={<Heading size={smallTextSize}>{t(headerKey)}</Heading>}
          description={
            <>
              <div className={styles.containerMargin}>
                <Text size={smallTextSize}>{t(errorKey, { merchantName: checkout?.merchant?.name })}</Text>
              </div>
              <CloseCheckout {...closeButtonProps} onClick={() => handleBackToStoreButtonClick()}>
                {t('shippingOption:backToStore')}
              </CloseCheckout>
            </>
          }
        />
      </div>
    )
  }

  const showAddressShippingOptionError = (): ReactNode => {
    // TODO: Refactor this block once inlineArcadeComponentsEnabled is enabled 100% in US.
    if (isInlineArcadeComponentsEnabled) {
      return null
    }

    if (!isAPTheme) {
      const onEditAddressClick = (): void => {
        router.push(Route.SHIPPING_ADDRESS)
      }

      return (
        <Message
          kind='Success'
          iconName='Warning'
          heading={t('shippingOption:error.retryShippingAddress.heading')}
          description={
            <Text renderAs='span' size={smallTextSize}>
              <Trans
                components={{
                  shippingAddressLink: <Link onClick={onEditAddressClick} kind='Underline' />,
                }}
                i18nKey={t('shippingOption:error.retryShippingAddress.message')}
              />
            </Text>
          }
          testNameSpace='shipping-option-error'
        />
      )
    }

    return (
      <ModuleContainer padding={{ vertical: 'Large', horizontal: 'XLarge' }}>
        <Grid center gutter='none'>
          <Grid.FixedItem width={70}>
            <div className={styles.alignImage}>
              <Image
                src={emptyBoxSrc}
                alt=''
                width='41'
                height='48'
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
            </div>
          </Grid.FixedItem>
          <span className={styles.contentPadding}>
            <Text size={smallTextSize} color='Gray40' renderAs='div'>
              {t('shippingOption:error.retryShippingAddress.message')}
            </Text>
          </span>
        </Grid>
      </ModuleContainer>
    )
  }

  const showShippingOptionUpdateInvalidError = (): ReactNode =>
    errorWithRetry('shippingOption:error.shippingOptionUpdateInvalid.heading', 'shippingOption:error.shippingOptionUpdateInvalid.message')

  const showShippingOptionSomethingWentWrongError = (): ReactNode => {
    if (isInlineArcadeComponentsEnabled) {
      return null
    }
    return errorWithRetry('shippingOption:error.unableToUpdate.heading', 'shippingOption:error.unableToUpdate.message')
  }

  const errorWithRetry = (headerKey: string, errorKey: string): ReactNode => {
    const retryButtonProps: ButtonProps = isAPTheme
      ? { kind: 'ErrorSecondary' as ButtonKinds, size: 'Small' as ButtonSizes }
      : { kind: 'Secondary' as ButtonKinds }
    const closeButtonProps: ButtonProps = isAPTheme ? { kind: 'Error' as ButtonKinds, size: 'Small' as ButtonSizes } : { kind: 'ErrorSecondary' as ButtonKinds }

    if (isInlineArcadeComponentsEnabled) {
      return (
        <MessageInline
          kind='Error'
          description={
            <Text size={smallTextSize}>
              <Trans
                components={{
                  retryLink: <Link onClick={handleRetryClick} kind='Underline' testNameSpace='shipping-option-error-inline-message' />,
                }}
                i18nKey={errorKey}
                values={{ merchantName: checkout?.merchant?.name }}
                context='inline'
              />
            </Text>
          }
          testNameSpace='shipping-option-error'
        />
      )
    }

    return (
      <div className={styles.containerMargin}>
        <Message
          kind='Error'
          heading={<Heading size={smallTextSize}>{t(headerKey)}</Heading>}
          description={
            <>
              <div className={styles.containerMargin}>
                <Text size={smallTextSize}>{t(errorKey)}</Text>
              </div>
              <Button {...retryButtonProps} onClick={handleRetryClick}>
                {t('shippingOption:retry')}
              </Button>
              <CloseCheckout {...closeButtonProps} onClick={() => handleBackToStoreButtonClick()}>
                {t('shippingOption:backToStore')}
              </CloseCheckout>
            </>
          }
        />
      </div>
    )
  }

  const renderError = (): ReactNode => {
    if (isPickup && error === TerminalErrorCode.InvalidPickupAddress) {
      return showPickupError()
    } else if (isPickup && error === ShippingOptionErrorType.TotalTooHighToLoadAnyOption) {
      return showPickupTotalTooHighError()
    } else if (isDeferredShipping && error === TerminalErrorCode.UnableToUpdateCheckout) {
      return showShippingOptionSomethingWentWrongError()
    } else if (isShippingOptionErrorType(error)) {
      if (isShippingAddressError) {
        return showAddressShippingOptionError()
      } else if (error === ShippingOptionErrorType.TotalTooHighToLoadAnyOption) {
        return showTotalTooHighError()
      } else if (error === ShippingOptionErrorType.ShippingOptionUpdateInvalid) {
        return showShippingOptionUpdateInvalidError()
      } else if (error === ShippingOptionErrorType.ShippingOptionUnableToUpdate) {
        return showShippingOptionSomethingWentWrongError()
      }
    }

    return showDefaultShippingOptionError()
  }

  const getErrorEventProps = (): ShippingOptionErrorEventProps | void => {
    if (error === ShippingOptionErrorType.TotalTooHighToLoadAnyOption) {
      return { otb: otbAmount }
    }
  }

  return (
    <>
      {renderError()}
      <TrackOnMount eventName={`${AmplitudeErrorEventNames[error] ?? error}`} eventProperties={getErrorEventProps() ?? undefined} />
    </>
  )
}
